/**
 * Subject syllabus tree aligned with GET /subject-details/:id (user API).
 *
 * Expected shape:
 * data.topics[].subTopics[].chapters[].sections[]
 *
 * Picker & filter sidebar: Topic → Subtopic → Chapter → Section.
 * Selectable leaves: sections when present; otherwise chapter; otherwise subtopic/topic-only.
 *
 * Questions API: topicIds, chapterIds, subtopicIds, sectionIds (see getQuestions).
 */

/** Shown in picker + filter header. */
export const EXAM_SYLLABUS_LEVELS = ['Topic', 'Subtopic', 'Chapter', 'Section']

function leafNode(node, topicId, depth, paramScope) {
  const id = String(node._id)
  return {
    id,
    label: node.title || node.name || 'Untitled',
    topicId,
    depth,
    paramScope,
    isSelectableLeaf: true,
    topicOnly: paramScope === 'topic',
  }
}

function mapChapter(ch, topicId) {
  const sections = ch.sections ?? []
  if (!sections.length) {
    return leafNode(ch, topicId, 2, 'chapter')
  }
  return {
    id: String(ch._id),
    label: ch.title || ch.name || 'Untitled',
    topicId,
    depth: 2,
    paramScope: 'chapter',
    isSelectableLeaf: false,
    children: sections.map((sec) => leafNode(sec, topicId, 3, 'section')),
  }
}

function mapSubTopics(topicId, subTopics) {
  return (subTopics ?? []).map((st) => {
    const chapters = st.chapters ?? []
    if (!chapters.length) {
      return leafNode(st, topicId, 1, 'subtopic')
    }
    return {
      id: String(st._id),
      label: st.title || st.name || 'Untitled',
      topicId,
      depth: 1,
      paramScope: 'subtopic',
      isSelectableLeaf: false,
      children: chapters.map((ch) => mapChapter(ch, topicId)),
    }
  })
}

/**
 * @param {object} detail — return value of getSubjectDetailsById (`{ data: { topics } }`)
 */
export function buildPickerTreeFromSubjectDetail(detail) {
  if (!detail) return []
  const root = detail.data != null ? detail.data : detail
  const topics = root.topics ?? []

  return topics.map((topic) => {
    const topicId = String(topic._id)
    const title = topic.title || topic.name || 'Untitled'
    const subTopics = topic.subTopics ?? []

    if (!subTopics.length) {
      return {
        id: `topic-only-${topicId}`,
        label: title,
        topicId,
        depth: 0,
        paramScope: 'topic',
        isSelectableLeaf: true,
        topicOnly: true,
      }
    }

    return {
      id: `topic-${topicId}`,
      label: title,
      topicId,
      depth: 0,
      paramScope: 'topic',
      isSelectableLeaf: false,
      children: mapSubTopics(topicId, subTopics),
    }
  })
}

/** Fallback: topics from subject list (no subTopics). */
export function buildPickerTreeFromTopicList(topics) {
  if (!topics?.length) return []
  return topics.map((topic) => {
    const topicId = String(topic._id)
    const title = topic.title || topic.name || 'Untitled'
    return {
      id: `topic-only-${topicId}`,
      label: title,
      topicId,
      depth: 0,
      paramScope: 'topic',
      isSelectableLeaf: true,
      topicOnly: true,
    }
  })
}

export function levelAriaLabel(depth, levelLabels = EXAM_SYLLABUS_LEVELS) {
  return levelLabels[Math.min(depth, levelLabels.length - 1)]
}

/**
 * Maps checkbox keys → API id sets. Includes subtopic **branch** ids (whole unit selected)
 * as well as chapter / subtopic leaves.
 */
export function collectLeafScopeSets(treeNodes) {
  const chapterIds = new Set()
  const subtopicIds = new Set()
  const sectionIds = new Set()
  const walk = (nodes) => {
    ;(nodes || []).forEach((n) => {
      if (n.isSelectableLeaf) {
        if (n.paramScope === 'chapter') chapterIds.add(n.id)
        else if (n.paramScope === 'subtopic') subtopicIds.add(n.id)
        else if (n.paramScope === 'section') sectionIds.add(n.id)
      } else {
        if (n.paramScope === 'subtopic' && n.id) subtopicIds.add(n.id)
        if (n.paramScope === 'chapter' && n.id) chapterIds.add(n.id)
      }
      if (n.children?.length) walk(n.children)
    })
  }
  walk(treeNodes)
  return { chapterIds, subtopicIds, sectionIds }
}

/**
 * Root topic _ids for every syllabus node that is checked. Supports multi-topic
 * sidebar selection (comma topicIds on /questions).
 */
export function collectTopicIdsFromSelectedKeys(selectedChapters, treeNodes) {
  const set = new Set()
  const walk = (nodes) => {
    ;(nodes || []).forEach((n) => {
      if (selectedChapters?.[n.id] && n.topicId != null) {
        set.add(String(n.topicId))
      }
      if (n.children?.length) walk(n.children)
    })
  }
  walk(treeNodes)
  return set
}

/**
 * Builds question-list query ids from the syllabus picker selection.
 *
 * Rules (to align UI selection with backend expectations):
 * - If "All units selected" (all terminal nodes), return no ids (subjectId-only scope).
 * - For a fully selected Topic: include only its topicId (do NOT include any child ids).
 * - For a partially selected Topic: do NOT include its topicId; include only the checked
 *   child unit ids (subtopic/chapter/section), preferring higher-level checked nodes
 *   (e.g., a checked Subtopic branch suppresses its checked Chapters/Sections).
 *
 * @returns {{ allUnitsSelected: boolean, topicIds: string[], subtopicIds: string[], chapterIds: string[], sectionIds: string[] }}
 */
export function collectQuestionFilterIdsFromSyllabusSelection(
  selectedChapters,
  treeNodes,
) {
  const empty = {
    allUnitsSelected: false,
    topicIds: [],
    subtopicIds: [],
    chapterIds: [],
    sectionIds: [],
  }

  if (!treeNodes?.length) return empty

  if (allPickerLeavesSelected(treeNodes, selectedChapters)) {
    return { ...empty, allUnitsSelected: true }
  }

  const topicIds = new Set()
  const subtopicIds = new Set()
  const chapterIds = new Set()
  const sectionIds = new Set()

  const collectCheckedUnits = (node) => {
    if (!node) return

    const state = getPickerTreeCheckState(node, selectedChapters)
    if (state === 'unchecked') return

    if (state === 'checked') {
      if (node.paramScope === 'subtopic') subtopicIds.add(String(node.id))
      else if (node.paramScope === 'chapter') chapterIds.add(String(node.id))
      else if (node.paramScope === 'section') sectionIds.add(String(node.id))
      return
    }

    ;(node.children || []).forEach(collectCheckedUnits)
  }

  ;(treeNodes || []).forEach((topicNode) => {
    const state = getPickerTreeCheckState(topicNode, selectedChapters)

    if (state === 'checked') {
      if (topicNode.topicId != null) topicIds.add(String(topicNode.topicId))
      return
    }

    if (state === 'indeterminate') {
      ;(topicNode.children || []).forEach(collectCheckedUnits)
    }
  })

  return {
    allUnitsSelected: false,
    topicIds: [...topicIds].sort(),
    subtopicIds: [...subtopicIds].sort(),
    chapterIds: [...chapterIds].sort(),
    sectionIds: [...sectionIds].sort(),
  }
}

/**
 * Builds the nested `topics` payload for POST `/api/user/questions/search`.
 *
 * Semantics expected by backend:
 * - OR across topic branches
 * - AND down each branch path
 *
 * Payload rule:
 * - If a node is fully selected (checked), stop there (do not include its children).
 * - If a node is indeterminate, include only selected descendants.
 * - If "all units selected", return `topics: undefined` (subject-only scope).
 *
 * @returns {{ allUnitsSelected: boolean, topics?: Array }}
 */
export function buildQuestionTreeSearchTopicsPayload(selectedChapters, treeNodes) {
  if (!treeNodes?.length) return { allUnitsSelected: false, topics: undefined }

  if (allPickerLeavesSelected(treeNodes, selectedChapters)) {
    return { allUnitsSelected: true, topics: undefined }
  }

  const buildChapterNode = (ch) => {
    const state = getPickerTreeCheckState(ch, selectedChapters)
    if (state === 'unchecked') return null
    if (state === 'checked') return { chapterId: String(ch.id) }

    const sections = (ch.children || [])
      .filter((sec) => Boolean(selectedChapters?.[sec.id]))
      .map((sec) => ({ sectionId: String(sec.id) }))

    if (sections.length === 0) return null
    return { chapterId: String(ch.id), sections }
  }

  const buildSubtopicNode = (st) => {
    const state = getPickerTreeCheckState(st, selectedChapters)
    if (state === 'unchecked') return null
    if (state === 'checked') return { subtopicId: String(st.id) }

    const chapters = (st.children || [])
      .map(buildChapterNode)
      .filter(Boolean)

    if (chapters.length === 0) return null
    return { subtopicId: String(st.id), chapters }
  }

  const buildTopicNode = (t) => {
    const state = getPickerTreeCheckState(t, selectedChapters)
    if (state === 'unchecked') return null

    const topicId = t.topicId != null ? String(t.topicId) : null
    if (!topicId) return null

    if (state === 'checked') return { topicId }

    const subtopics = (t.children || [])
      .map(buildSubtopicNode)
      .filter(Boolean)

    if (subtopics.length === 0) return null
    return { topicId, subtopics }
  }

  const topics = (treeNodes || []).map(buildTopicNode).filter(Boolean)
  return { allUnitsSelected: false, topics: topics.length ? topics : undefined }
}

function findNodeInPickerTree(nodes, predicate) {
  for (const n of nodes || []) {
    if (predicate(n)) return n
    if (n.children?.length) {
      const found = findNodeInPickerTree(n.children, predicate)
      if (found) return found
    }
  }
  return null
}

/** Every node id in `node` and its descendants (matches sidebar “select subtree”). */
export function collectSubtreeNodeIds(node) {
  const ids = [node.id]
  if (node.children?.length) {
    node.children.forEach((c) => {
      ids.push(...collectSubtreeNodeIds(c))
    })
  }
  return ids
}

/** All node ids in the picker/filter tree (for “select all”). */
export function collectAllPickerNodeIds(nodes) {
  const ids = []
  const walk = (list) => {
    ;(list || []).forEach((n) => {
      ids.push(n.id)
      if (n.children?.length) walk(n.children)
    })
  }
  walk(nodes)
  return ids
}

/** Terminal nodes (no children) — selectable unit rows for counts. */
export function collectPickerLeafIds(nodes) {
  const ids = []
  const walk = (list) => {
    ;(list || []).forEach((n) => {
      if (!n.children?.length) ids.push(n.id)
      else walk(n.children)
    })
  }
  walk(nodes)
  return ids
}

/** @returns {'checked' | 'unchecked' | 'indeterminate'} */
export function getPickerTreeCheckState(node, selected) {
  if (!node.children?.length) {
    return selected[node.id] ? 'checked' : 'unchecked'
  }
  const childStates = node.children.map((c) => getPickerTreeCheckState(c, selected))
  const allChecked = childStates.every((s) => s === 'checked')
  const allUnchecked = childStates.every((s) => s === 'unchecked')
  if (allChecked) return 'checked'
  if (allUnchecked) return 'unchecked'
  return 'indeterminate'
}

export function countPickerSelectedLeaves(nodes, selected) {
  let n = 0
  const walk = (list) => {
    ;(list || []).forEach((node) => {
      if (node.children?.length) walk(node.children)
      else if (selected[node.id]) n += 1
    })
  }
  walk(nodes)
  return n
}

export function allPickerLeavesSelected(nodes, selected) {
  const leaves = collectPickerLeafIds(nodes)
  return leaves.length > 0 && leaves.every((id) => selected[id])
}

export function somePickerLeafSelected(nodes, selected) {
  const leaves = collectPickerLeafIds(nodes)
  return leaves.some((id) => selected[id])
}

/** Stable primary topic for route `topicId` when multiple topics are checked. */
export function pickPrimaryTopicIdFromSelection(selectedChapters, treeNodes) {
  const set = collectTopicIdsFromSelectedKeys(selectedChapters, treeNodes)
  if (set.size > 0) return [...set].sort()[0]
  return null
}

/**
 * Checkbox map for FilterTopicTree from exam-papers navigation state.
 * Mirrors what the tree would set if the user toggled the same scope.
 */
export function buildSelectedChaptersMapFromRouteState(state, treeNodes) {
  if (
    state?.pickerSelectedChapters &&
    typeof state.pickerSelectedChapters === 'object'
  ) {
    const next = { ...state.pickerSelectedChapters }
    const has = Object.keys(next).some((k) => next[k])
    if (has) return next
  }
  if (!state?.topicId || !treeNodes?.length) return {}
  const topicId = String(state.topicId)
  const sectionId = state.sectionId != null ? String(state.sectionId) : null
  const chapterId = state.chapterId != null ? String(state.chapterId) : null
  const subtopicId = state.subtopicId != null ? String(state.subtopicId) : null

  if (sectionId) {
    const node = findNodeInPickerTree(treeNodes, (n) => n.id === sectionId)
    if (node) {
      const ids = collectSubtreeNodeIds(node)
      return Object.fromEntries(ids.map((id) => [id, true]))
    }
    return { [sectionId]: true }
  }
  if (chapterId) {
    const node = findNodeInPickerTree(treeNodes, (n) => n.id === chapterId)
    if (node) {
      const ids = collectSubtreeNodeIds(node)
      return Object.fromEntries(ids.map((id) => [id, true]))
    }
    return { [chapterId]: true }
  }
  if (subtopicId) {
    const node = findNodeInPickerTree(treeNodes, (n) => n.id === subtopicId)
    if (node) {
      const ids = collectSubtreeNodeIds(node)
      return Object.fromEntries(ids.map((id) => [id, true]))
    }
    return { [subtopicId]: true }
  }

  const topicNode = findNodeInPickerTree(
    treeNodes,
    (n) => n.id === `topic-${topicId}` || n.id === `topic-only-${topicId}`,
  )
  if (!topicNode) return {}

  const ids = collectSubtreeNodeIds(topicNode)
  return Object.fromEntries(ids.map((id) => [id, true]))
}
